import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { applyLaneLayout } from "../layout/lanes";
// Module-local frozen empties: returning these instead of unpositioned nodes
// during the brief window before ELK/savedPositions resolve prevents React
// Flow from painting every node at (0,0) and snapping on next tick.
const EMPTY_NODES = Object.freeze([]);
const EMPTY_EDGES = Object.freeze([]);
/**
 * A render is "ready" when we can place every live node via either the cached
 * ELK output or caller-supplied saved positions. Covers both the fresh-publish
 * case (saved positions only, no cache yet) and the first-load case (ELK just
 * resolved). Exported for unit testing — the hook uses this same predicate.
 */ export function positionsReady(nodes, cachedPositions, savedPositions) {
    if (nodes.length === 0) return true;
    const hasFromCache = !!cachedPositions && nodes.every((n)=>cachedPositions.has(n.id));
    if (hasFromCache) return true;
    const hasFromSaved = !!savedPositions && nodes.every((n)=>savedPositions.has(n.id));
    return hasFromSaved;
}
export function useFlow(options) {
    const { adapter, source, cacheKey, enabled = true, savedPositions, laneLayout } = options;
    if (process.env.NODE_ENV !== "production" && enabled && cacheKey.length === 0) {
        console.warn("[flow-kit] useFlow was called with an empty cacheKey. The layout query will never invalidate when `source` changes — include identifying fields (id, updatedAt, layout flags) in cacheKey.");
    }
    // Live build — cheap (O(n), no ELK). Runs every time `source` changes so
    // `node.data` always reflects the current source content.
    const liveGraph = useMemo(()=>adapter.build(source), [
        adapter,
        source
    ]);
    // Cache ONLY the ELK-positioned output, keyed by structure. Content changes
    // don't affect the cache key — they flow through `liveGraph` instead.
    const { data: cachedPositions, isLoading } = useQuery({
        queryKey: [
            "flow-kit",
            "layout",
            ...cacheKey
        ],
        queryFn: async ()=>{
            const assembly = adapter.build(source);
            const byId = new Map();
            if (assembly.nodes.length === 0) return {
                byId
            };
            const positioned = await adapter.layout.run(assembly);
            for (const n of positioned.nodes){
                byId.set(n.id, {
                    x: n.position.x,
                    y: n.position.y
                });
            }
            return {
                byId
            };
        },
        enabled,
        staleTime: Infinity,
        structuralSharing: false
    });
    // Gate the render on positions being resolvable for every live node.
    // Otherwise a transient window after source updates paints nodes at (0,0)
    // (their build-time fallback) before ELK or savedPositions land, producing
    // a visible "squash → snap" flash. Returning empty keeps the canvas blank
    // instead; callers observe `isLoading` to render a skeleton.
    const ready = useMemo(()=>positionsReady(liveGraph.nodes, cachedPositions?.byId, savedPositions), [
        liveGraph.nodes,
        cachedPositions,
        savedPositions
    ]);
    const nodes = useMemo(()=>{
        if (!ready) return EMPTY_NODES;
        // Precedence: savedPositions > cached ELK positions > build-time position.
        const elkPositions = cachedPositions?.byId;
        const overlaid = liveGraph.nodes.map((n)=>{
            const saved = savedPositions?.get(n.id);
            if (saved) return {
                ...n,
                position: {
                    x: saved.x,
                    y: saved.y
                }
            };
            const elk = elkPositions?.get(n.id);
            if (elk) return {
                ...n,
                position: {
                    x: elk.x,
                    y: elk.y
                }
            };
            return n;
        });
        if (!laneLayout) return overlaid;
        return applyLaneLayout(overlaid, laneLayout);
    }, [
        ready,
        liveGraph,
        cachedPositions,
        savedPositions,
        laneLayout
    ]);
    const edges = ready ? liveGraph.edges : EMPTY_EDGES;
    return {
        nodes,
        edges,
        isLoading: isLoading || !ready
    };
}

//# sourceMappingURL=useFlow.js.map